import { useEffect, useMemo, useState } from 'react'

import type { Activity, FitMessage } from '@fit-editor/fit-core'

import {
  developerFieldIndex,
  listMessageGroups,
  messagesByKey,
  unknownFieldBaseTypes,
} from '@/lib/fit-data'
import type { DeveloperFieldInfo, MessageGroup } from '@/lib/fit-data'
import { loadFitProfile } from '@/lib/fit-profile'
import type { FitProfileIndex } from '@/lib/fit-profile'

export interface FitProfileState {
  profile: FitProfileIndex | null
  error: boolean
}

/** Loads the (lazily code-split) FIT Profile index once. */
export function useFitProfile(): FitProfileState {
  const [state, setState] = useState<FitProfileState>({ profile: null, error: false })
  useEffect(() => {
    let cancelled = false
    loadFitProfile().then(
      (profile) => {
        if (!cancelled) setState({ profile, error: false })
      },
      () => {
        if (!cancelled) setState({ profile: null, error: true })
      },
    )
    return () => {
      cancelled = true
    }
  }, [])
  return state
}

export interface FitDataModel {
  activity: Activity
  profile: FitProfileIndex | null
  byKey: Map<string, FitMessage[]>
  groups: MessageGroup[]
  devFields: Map<number, DeveloperFieldInfo>
  unknownBaseTypes: Map<string, Map<number, string>>
}

/**
 * Derived per-activity lookup tables, memoized on the activity object
 * identity (each edit/undo yields a new replayed `Activity`; the original
 * snapshot is stable) so nothing is recomputed per render.
 */
export function useFitDataModel(activity: Activity, profile: FitProfileIndex | null): FitDataModel {
  return useMemo(
    () => ({
      activity,
      profile,
      byKey: messagesByKey(activity),
      groups: listMessageGroups(activity, profile),
      devFields: developerFieldIndex(activity),
      unknownBaseTypes: unknownFieldBaseTypes(activity),
    }),
    [activity, profile],
  )
}
